import { useState } from "react";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";

export function useExcelMappingImport({
    notify,
    modelFields,
    templateHeaders,
    templateSheetName,
    templateFilename,
    mapMappedRow,
    isMappedRowValid,
    onImportRows,
}) {
    const { t } = useTranslation();
    const [showMapping, setShowMapping] = useState(false);
    const [fileColumns, setFileColumns] = useState([]);
    const [fileData, setFileData] = useState([]);
    const [showSheetSelection, setShowSheetSelection] = useState(false);
    const [sheetNames, setSheetNames] = useState([]);
    const [currentWorkbook, setCurrentWorkbook] = useState(null);

    const processRows = (rows) => {
        if (!rows.length) {
            throw new Error(t('importExportButtons.selectedSheetEmpty'));
        }
        const columns = rows[0].map((c) => String(c || "").trim()).filter(Boolean);
        const dataRows = rows.slice(1).map((row) =>
            Object.fromEntries(columns.map((col, i) => [col, row[i]]))
        );
        setFileColumns(columns);
        setFileData(dataRows);
        setShowMapping(true);
    };

    const processSheetData = (workbook, sheetName) => {
        const sheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
        processRows(rows);
    };

    const handleSheetSelect = (sheetName) => {
        try {
            processSheetData(currentWorkbook, sheetName);
            setShowSheetSelection(false);
            setCurrentWorkbook(null);
            setSheetNames([]);
        } catch (error) {
            notify(error.message || t('useExcelMappingImport.sheetReadError'), "", 3);
        }
    };

    const handleCancelSheetSelection = () => {
        setShowSheetSelection(false);
        setCurrentWorkbook(null);
        setSheetNames([]);
    };

    const parseCsvRows = async (file) => {
        const text = await file.text();
        const lines = text.split("\n").filter(Boolean);
        const columns = (lines[0] || "").split(";").map((c) => c.trim());
        return [columns, ...lines.slice(1).map((line) => line.split(";").map((v) => (v || "").trim()))];
    };

    const handleImportFileChange = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = null;
        if (!file) return;

        try {
            if (file.name.endsWith(".csv")) {
                const rows = await parseCsvRows(file);
                processRows(rows);
                return;
            }

            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { type: "array", cellDates: true });
            const names = workbook.SheetNames || [];
            if (!names.length) {
                throw new Error(t('importExportButtons.noSheetInFile'));
            }
            if (names.length > 1) {
                setCurrentWorkbook(workbook);
                setSheetNames(names);
                setShowSheetSelection(true);
                return;
            }
            processSheetData(workbook, names[0]);
        } catch (error) {
            notify(error.message || t('useExcelMappingImport.excelImportError'), "", 3);
        }
    };

    const generateDemoRowsFromConfig = (fields, headers) => {
        const fieldsToUse = fields && fields.length > 0
            ? fields
            : headers.map(h => ({ name: h, string: h }));

        const getDemoVal = (field, index) => {
            const fName = (field.name || field.string || field || "").toString().toLowerCase();
            const fLabel = (field.string || field.name || "").toString().toLowerCase();

            if (fName.includes("matricul") || fName.includes("identifi")) {
                return index === 1 ? "EMP001" : "EMP002";
            }
            if (fName.includes("name") || fName.includes("nom") || fLabel.includes("employé")) {
                return index === 1 ? "Jean Dupont" : "Marie Martin";
            }
            if (fName.includes("days") || fName.includes("jour")) {
                return index === 1 ? 22 : 20;
            }
            if (fName.includes("amount") || fName.includes("montant") || fName.includes("salary") || fName.includes("gross") || fName.includes("brut")) {
                return index === 1 ? 3500.00 : 2800.00;
            }
            if (fName.includes("date")) {
                return index === 1 ? "01/2024" : "02/2024";
            }
            return index === 1 ? "Exemple 1" : "Exemple 2";
        };

        const row1 = {};
        const row2 = {};

        headers.forEach(header => {
            const matchedField = fieldsToUse.find(f => (f.string || f.name) === header) || { name: header, string: header };
            row1[header] = getDemoVal(matchedField, 1);
            row2[header] = getDemoVal(matchedField, 2);
        });

        return [row1, row2];
    };

    const handleDownloadTemplate = () => {
        const sampleRows = generateDemoRowsFromConfig(modelFields, templateHeaders);
        const ws = XLSX.utils.json_to_sheet(sampleRows, { header: templateHeaders });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, templateSheetName);
        XLSX.writeFile(wb, templateFilename);
    };

    const handleConfirmMapping = async (mapping) => {
        try {
            const mappedRows = fileData.map((row) => {
                const obj = {};
                Object.entries(mapping).forEach(([col, field]) => {
                    if (field) obj[field] = row[col];
                });
                return obj;
            });

            const normalizedRows = mappedRows.map((row) => mapMappedRow(row)).filter((row) => isMappedRowValid(row));

            if (!normalizedRows.length) {
                throw new Error(t('useExcelMappingImport.noValidRowAfterMapping'));
            }

            await onImportRows(normalizedRows);
            setShowMapping(false);
            setFileData([]);
            setFileColumns([]);
        } catch (error) {
            notify(error.message || t('useExcelMappingImport.mappedImportError'), "", 3);
        }
    };

    const handleTestMapping = (mapping) => {
        const mappedRows = fileData.map((row) => {
            const obj = {};
            Object.entries(mapping).forEach(([col, field]) => {
                if (field) obj[field] = row[col];
            });
            return obj;
        });
        const validRows = mappedRows.map((row) => mapMappedRow(row)).filter((row) => isMappedRowValid(row));
        notify(t('useExcelMappingImport.validRowsDetected', { count: validRows.length }), "", 2);
    };

    const handleCancelMapping = () => {
        setShowMapping(false);
        setFileData([]);
        setFileColumns([]);
    };

    return {
        modelFields,
        showMapping,
        fileColumns,
        showSheetSelection,
        sheetNames,
        handleImportFileChange,
        handleSheetSelect,
        handleCancelSheetSelection,
        handleDownloadTemplate,
        handleConfirmMapping,
        handleTestMapping,
        handleCancelMapping,
    };
}
